import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://business.clever-booking.com';

// ─── Shared fetch wrapper (always sends the session cookie) ────────────

async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Accept-Language': navigator.language || 'en',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    const err = new Error(text || `${r.status} ${r.statusText}`) as Error & { status: number };
    err.status = r.status;
    throw err;
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

// ─── Types ─────────────────────────────────────────────────────────────

export interface MarketplaceSubcategory {
  key: string;
  displayName: string;
}

export interface MarketplaceCategory {
  key: string;
  displayName: string;
  icon: string | null;
  subcategories: MarketplaceSubcategory[];
}

export interface SalonSearchResult {
  tenantId: string;
  /** Stable per-row id (tenantId:locationId) — tenantId repeats across a salon's location rows. */
  key: string;
  subdomain: string;
  name: string;
  bio: string | null;
  coverImageUrl: string | null;
  featured: boolean;
  distanceKm: number | null;
  /** Tenant display currency (ISO 4217, e.g. "EUR", "BRL") for `topServices[].price`. */
  currency?: string;
  topServices: { name: string; price: number; durationMinutes: number }[];
  location: { name: string; address: string; lat: number | null; lng: number | null } | null;
  isMobile?: boolean;
  serviceArea?: string;
}

export interface SearchResponse {
  results: SalonSearchResult[];
  total: number;
  page: number;
  size: number;
}

export interface SalonLocation {
  id: string;
  name: string;
  address: string;
  phone: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string | null;
}

export interface SalonService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number;
  requiresDeposit: boolean;
  depositAmount: number | null;
  /** True when the tenant offers this service at the customer's location (satellite/mobile). */
  mobileEligible?: boolean;
  /** Pre-resolved travel fee for this service, if mobileEligible. Null = no fee configured. */
  resolvedRelocationFee?: number | null;
  /**
   * True when the tenant + service support ONLINE bookings (Google Meet video
   * call). Combined with tenant.onlineBookingsEnabled — both must be true for
   * the ModeChooser to surface the Video-call option. (#249 A3+)
   */
  onlineEligible?: boolean;
  /**
   * Fixed locations this service is explicitly offered at. Empty = offered
   * everywhere; combined with mobile/onlineEligible it marks a service as
   * alternative-mode-only (no AT_LOCATION option). Mirrors the tenant
   * booking page contract.
   */
  locationIds?: string[];
  /** Audience flags (V92): MEN/WOMEN/KIDS the service is aimed at; empty = everyone. */
  audiences?: string[];
  /**
   * Amount the customer must prepay for this service when the salon has
   * prepayment enabled (Task 10 BE). Currency comes from
   * salon.prepayment.currency. Used to drive the "Pay now ({amountDue})"
   * action + the BookingPaymentStep amount.
   */
  amountDue?: number;
}

export interface SalonServiceCategory {
  key: string;
  displayName: string;
  services: SalonService[];
}

export interface SalonProfile {
  subdomain: string;
  name: string;
  bio: string | null;
  coverImageUrl: string | null;
  featured: boolean;
  /**
   * Tenant display currency (ISO 4217, e.g. "EUR", "BRL") for every service
   * price / fee on the profile. Distinct from `prepayment.currency`, which is
   * the online-payment currency and only applies to prepay amounts.
   */
  currency?: string;
  website: string | null;
  locations: SalonLocation[];
  serviceCategories: SalonServiceCategory[];
  /**
   * Tenant-level flag for satellite/mobile bookings — true when at least one
   * service is mobile-eligible. Exposed by MarketplaceResource. (#246/#247)
   */
  mobileBookingsEnabled?: boolean;
  /**
   * Tenant-level flag for ONLINE (video-call) bookings. Combined with
   * service.onlineEligible to decide whether to show the Video-call mode in
   * the ModeChooser. Exposed by MarketplaceResource. (#249 A3+)
   */
  onlineBookingsEnabled?: boolean;
  /**
   * Tenant-level IANA timezone (branding tz, else first FIXED location). Used
   * to convert ONLINE slot times when no physical location is selected — the
   * hidden dispatch location no longer appears in `locations`.
   */
  timezone?: string | null;
  /**
   * Salon-level prepayment configuration (Task 10 BE). When `enabled`, the
   * booking dialog branches into the prepayment flow: customers pay up-front
   * via Stripe. `allowPayAtLocation` adds a second "pay at the location"
   * action alongside the pay-now path; when false, only prepayment is allowed
   * (a plain book would be 402-rejected). Absent/undefined = prepayment off.
   */
  prepayment?: {
    enabled: boolean;
    allowPayAtLocation: boolean;
    currency: string;
    // Effective gate from the server: prepayment is enabled AND the salon has a
    // payout-ready Stripe Connect account. When false, the salon can't actually
    // receive online payments, so the UI must not offer them.
    paymentsAvailable?: boolean;
  };
}

export interface MarketplaceUser {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  city: string | null;
  language: string | null;
}

export interface MyBooking {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  tenant: { subdomain: string; name: string; listed?: boolean } | null;
  /** Salon display currency (ISO 4217) for `service.price`. */
  currency?: string;
  service: { id: string; name: string; price: number } | null;
  location: { id: string; name: string; address: string } | null;
}

// ─── Queries ───────────────────────────────────────────────────────────

export interface MarketplaceCity {
  name: string;
  salonCount: number;
}

/**
 * Distinct cities we have marketplace-listed salons in, ordered by
 * salon-count descending. Used to power the city autocomplete in the
 * search bar — it's a suggestion list, not a hard filter.
 */
export function useCities(country?: string | null) {
  const qs = new URLSearchParams();
  if (country) qs.set('country', country);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<MarketplaceCity[]>({
    queryKey: ['marketplace-cities', country ?? null],
    queryFn: () => api<MarketplaceCity[]>(`/api/v1/marketplace/cities${suffix}`),
    staleTime: 5 * 60_000,
  });
}

export function useCategories() {
  return useQuery<MarketplaceCategory[]>({
    queryKey: ['marketplace-categories'],
    queryFn: () => api<MarketplaceCategory[]>(`/api/v1/marketplace/categories?marketplace=beauty`),
    staleTime: 5 * 60_000,
  });
}

export interface MarketplaceCountry {
  code: string;
  salonCount: number;
}

/**
 * Countries we have marketplace-listed salons in, with salon counts. Powers
 * the country-domain landing pages. Country-independent (no filter param).
 */
export function useCountries() {
  return useQuery<MarketplaceCountry[]>({
    queryKey: ['marketplace-countries'],
    queryFn: () => api<MarketplaceCountry[]>(`/api/v1/marketplace/countries`),
    staleTime: 5 * 60_000,
  });
}

export interface SearchParams {
  category?: string | null;
  subcategory?: string | null;
  q?: string | null;
  city?: string | null;
  /**
   * ISO-2 (uppercase) country code derived from the hostname (BE/PT/FR/NL on
   * the country domains, null on salunas.com/localhost). When set, restricts
   * results to salons in that country. Maps to `country=XX` on
   * /marketplace/search.
   */
  country?: string | null;
  lat?: number | null;
  lng?: number | null;
  radius?: number | null;
  page?: number;
  size?: number;
  /**
   * When true, restrict results to salons that offer ONLINE (video-call)
   * bookings. Maps to `onlineAvailable=true` on /marketplace/search. (#249 A5)
   */
  onlineAvailable?: boolean;
  /** Narrows to salons offering mobile/home-visit bookings. Maps to mobileAvailable=true. */
  mobileAvailable?: boolean;
  /**
   * Audience facet (V92): CSV of MEN/WOMEN/KIDS. Keeps salons with at least one
   * service suiting every requested audience (unflagged services count as
   * "for everyone"). Maps to audience=... on /marketplace/search.
   */
  audience?: string | null;
}

export function useSearch(params: SearchParams) {
  const qs = new URLSearchParams();
  qs.set('marketplace', 'beauty');
  if (params.category) qs.set('category', params.category);
  if (params.subcategory) qs.set('subcategory', params.subcategory);
  if (params.q) qs.set('q', params.q);
  if (params.city) qs.set('city', params.city);
  if (params.country) qs.set('country', params.country);
  if (params.lat != null) qs.set('lat', String(params.lat));
  if (params.lng != null) qs.set('lng', String(params.lng));
  if (params.radius != null) qs.set('radius', String(params.radius));
  if (params.page != null) qs.set('page', String(params.page));
  if (params.onlineAvailable) qs.set('onlineAvailable', 'true');
  if (params.mobileAvailable) qs.set('mobileAvailable', 'true');
  if (params.audience) qs.set('audience', params.audience);
  qs.set('size', String(params.size ?? 24));

  return useQuery<SearchResponse>({
    queryKey: ['marketplace-search', qs.toString()],
    queryFn: () => api<SearchResponse>(`/api/v1/marketplace/search?${qs.toString()}`),
    staleTime: 60_000,
  });
}

export function useSalon(subdomain: string | undefined) {
  return useQuery<SalonProfile>({
    queryKey: ['marketplace-salon', subdomain],
    queryFn: () => api<SalonProfile>(`/api/v1/marketplace/salon/${subdomain}`),
    enabled: !!subdomain,
    staleTime: 60_000,
  });
}

/** Returns the signed-in user or null on 401 (resolved, not thrown). */
export function useMe() {
  return useQuery<MarketplaceUser | null>({
    queryKey: ['marketplace-me'],
    queryFn: async () => {
      try {
        const r = await api<{ marketplaceUser: MarketplaceUser }>(`/api/v1/marketplace/auth/me`);
        return r.marketplaceUser;
      } catch (e) {
        const err = e as { status?: number };
        if (err.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fullName: string | null; phone: string | null }) =>
      api<{ marketplaceUser: MarketplaceUser }>(`/api/v1/marketplace/auth/me`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: (r) => {
      // Reflect the saved profile immediately and refetch on next read.
      qc.setQueryData(['marketplace-me'], r.marketplaceUser);
      qc.invalidateQueries({ queryKey: ['marketplace-me'] });
    },
  });
}

export function useMyBookings(past = false) {
  return useQuery<MyBooking[]>({
    queryKey: ['marketplace-my-bookings', past],
    queryFn: async () => {
      const r = await api<{ bookings: MyBooking[] }>(
        `/api/v1/marketplace/me/bookings?past=${past}`,
      );
      return r.bookings;
    },
    staleTime: 30_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (email: string) =>
      api<void>(`/api/v1/marketplace/auth/request-magic-link`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
  });
}

export function useConsumeMagicLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ marketplaceUser: MarketplaceUser }>(
        `/api/v1/marketplace/auth/consume-magic-link`,
        {
          method: 'POST',
          body: JSON.stringify({ token }),
        },
      ),
    onSuccess: (data) => {
      qc.setQueryData(['marketplace-me'], data.marketplaceUser);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<void>(`/api/v1/marketplace/auth/logout`, { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(['marketplace-me'], null);
      qc.invalidateQueries({ queryKey: ['marketplace-my-bookings'] });
    },
  });
}

export interface BookingPayload {
  serviceId: string;
  // Nullable/optional: ONLINE and MOBILE bookings have no physical location.
  locationId?: string | null;
  employeeId?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  notes?: string;
  // Guest-only — ignored by BE when a session is present.
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  /**
   * Customer-chosen language at booking time (en/nl/fr/pt-BR). BE
   * normalizes and persists to appointments.customer_language; drives
   * the confirmation/cancellation email language.
   */
  language?: string;
  /**
   * Marketing opt-in checkbox state (#162). Tri-state on the wire:
   * omitted = no change to the customer's stored preference; true =
   * opt in; false = opt out. The FE only ever sends `true` when ticked
   * and otherwise omits the field, per spec ("Never silently downgrade").
   */
  marketingOptIn?: boolean;
  /**
   * AT_LOCATION (default), MOBILE, or ONLINE. Sent when the service supports
   * the chosen mode. ONLINE requires service.onlineEligible AND
   * tenant.onlineBookingsEnabled (BE re-validates).
   */
  bookingMode?: BookingMode;
  /** Customer's destination address. Only sent when bookingMode === 'MOBILE'. */
  customerAddress?: string;
  /**
   * Customer's IANA timezone (e.g. "America/Sao_Paulo"). REQUIRED when
   * bookingMode === 'ONLINE' — BE rejects with customer_timezone_required.
   * Auto-detected from the browser at booking time and editable on the
   * confirm step via the TimezoneConfirm control.
   */
  customerTimezone?: string;
  /**
   * Prepayment "pay at the location" path (Task 10/20). When the salon has
   * prepayment enabled AND allows pay-at-location, sending `true` books
   * without an up-front Stripe charge. Omitted on the normal/non-prepay path.
   * (A plain book against a prepay-required salon is 402-rejected by the BE.)
   */
  payAtLocation?: boolean;
  /**
   * REQUIRED by the BE — the customer must have agreed to the cancellation
   * policy. The booking endpoint rejects with 400 when absent/false. The UI
   * gates the Book button behind the policy checkbox, so this is always `true`.
   */
  agreedToPolicy?: boolean;
  /** Email-OTP proof from /booking-verifications/confirm; sent only when required. */
  verificationToken?: string;
}

/**
 * AT_LOCATION = in person at a fixed location.
 * MOBILE      = staff travels to the customer (#246/#247).
 * ONLINE      = video call via Google Meet (#249 A3+).
 */
export type BookingMode = 'AT_LOCATION' | 'MOBILE' | 'ONLINE';

export interface BookingResponse {
  bookingReference: string;
  appointmentId: string;
  status: string;
  approvalRequired: boolean;
  salon: { subdomain: string; name: string };
  serviceName: string;
  locationName: string;
  startTime: string;
  endTime: string;
  price: number;
  signedIn: boolean;
  /** Echoed by the BE when supported; falls back to the mode the FE sent. */
  bookingMode?: BookingMode;
  /**
   * Lifecycle of the online-meeting link.
   * NOT_APPLICABLE for non-ONLINE bookings; LINK_READY/LINK_PENDING for ONLINE.
   * Treated as LINK_PENDING when absent on an ONLINE booking.
   */
  linkState?: 'NOT_APPLICABLE' | 'LINK_READY' | 'LINK_PENDING';
  /**
   * Google Meet URL when linkState === 'LINK_READY'. Absent (or null) when the
   * link is still pending or the booking isn't ONLINE.
   */
  onlineMeetingUrl?: string | null;
  /** Echoed by the BE when supported; falls back to the customerTimezone the FE sent. */
  customerTimezone?: string | null;
}

export interface MarketplaceEmployee {
  id: string;
  firstName: string;
  lastName: string | null;
  role: string | null;
  avatar: string | null;
}

/**
 * Active employees at a marketplace salon, optionally filtered to those
 * qualified for a service AND who work at the given location.
 */
export function useEmployees(
  subdomain: string,
  params: { serviceId: string | null; locationId: string | null },
) {
  const enabled = !!(subdomain && params.serviceId && params.locationId);
  return useQuery<MarketplaceEmployee[]>({
    queryKey: ['marketplace-employees', subdomain, params.serviceId, params.locationId],
    enabled,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('serviceId', params.serviceId as string);
      qs.set('locationId', params.locationId as string);
      return api<MarketplaceEmployee[]>(
        `/api/v1/marketplace/salon/${subdomain}/employees?${qs.toString()}`,
      );
    },
    staleTime: 60_000,
  });
}

export interface AvailabilityResponse {
  date: string;
  slots: string[]; // "HH:mm"
}

/**
 * Returns the bookable slot list for a given salon + service + location +
 * date. `enabled` is wired via the optional `enabled` arg so the dialog
 * can defer the call until the user has picked a date.
 */
export function useAvailability(
  subdomain: string,
  params: {
    date: string | null;
    serviceId: string | null;
    locationId: string | null;
    employeeId?: string | null;
    /**
     * Optional. When set to MOBILE/ONLINE, BE applies mode-specific slot
     * filtering (e.g. ignores fixed-location bounds for MOBILE; treats the
     * service as remote for ONLINE).
     */
    bookingMode?: BookingMode;
  },
) {
  // MOBILE/ONLINE have no physical location — the BE only requires a
  // locationId for AT_LOCATION. Mirror that here so online/mobile-only salons
  // can still load slots (otherwise the query never runs and no times show).
  const locationless = params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE';
  const enabled = !!(
    params.date &&
    params.serviceId &&
    subdomain &&
    (params.locationId || locationless)
  );
  return useQuery<AvailabilityResponse>({
    queryKey: [
      'marketplace-availability',
      subdomain,
      params.date,
      params.serviceId,
      params.locationId,
      params.employeeId ?? null,
      params.bookingMode ?? null,
    ],
    enabled,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('date', params.date as string);
      qs.set('serviceId', params.serviceId as string);
      // Only send locationId when we actually have one (AT_LOCATION). For
      // MOBILE/ONLINE it's absent — sending "null" would 400 on the BE.
      if (params.locationId) qs.set('locationId', params.locationId);
      if (params.employeeId) qs.set('employeeId', params.employeeId);
      if (params.bookingMode === 'MOBILE' || params.bookingMode === 'ONLINE') {
        qs.set('bookingMode', params.bookingMode);
      }
      return api<AvailabilityResponse>(
        `/api/v1/marketplace/salon/${subdomain}/availability?${qs.toString()}`,
      );
    },
    staleTime: 30_000,
  });
}

export function useBook(subdomain: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BookingPayload) =>
      api<BookingResponse>(`/api/v1/marketplace/salon/${subdomain}/book`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketplace-my-bookings'] });
    },
  });
}

export interface StartVerificationResult {
  verificationRequired: boolean;
}

export function useStartBookingVerification() {
  return useMutation<StartVerificationResult, Error, { email: string; language?: string }>({
    mutationFn: (vars) =>
      api<StartVerificationResult>(`/api/v1/marketplace/booking-verifications/start`, {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
  });
}

export interface ConfirmVerificationResult {
  verificationToken: string;
}

export function useConfirmBookingVerification() {
  return useMutation<ConfirmVerificationResult, Error, { email: string; code: string }>({
    mutationFn: (vars) =>
      api<ConfirmVerificationResult>(`/api/v1/marketplace/booking-verifications/confirm`, {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
  });
}
